import { useState } from 'react';
import { Download } from 'lucide-react';
import { api } from '../../lib/api';
import { Chip, fmtDate, downloadFile } from '../bids/FieldGrid';
import { iconBtn, useList, Empty } from './shared';

const DOC_LABEL = {
  pds: 'Product data sheet', tds: 'Technical data sheet', sds: 'Safety data sheet', coa: 'Certificate of analysis', sieve_chart: 'Sieve chart',
  spec_section: 'Spec section', install_guide: 'Installation guide', brochure: 'Brochure', case_study: 'Case study', photo: 'Photo', color_chart: 'Colour chart', other: 'Other',
};

export default function DatasheetsTab() {
  const { rows, err, load, setErr } = useList('/marketing/datasheets', 'documents');
  const [busy, setBusy] = useState(null);
  const cleared = (rows || []).filter((d) => d.cleared_public);
  async function toggle(d) {
    setBusy(d.id);
    try { await api.patch(`/marketing/datasheets/${d.id}`, { cleared_public: !d.cleared_public }); await load(); } catch (e) { setErr(e.message); }
    setBusy(null);
  }
  return (
    <div className="space-y-3">
      <p className="text-xs text-[var(--text-secondary)]">
        Mark which library documents Concria has cleared for public use. The manifest lists them for a later website change that serves them behind the lead form.
        The website is not changed and no file is made public from here. Documents and files are managed in Bids &amp; Specs, Document library.
      </p>
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm">{cleared.length} cleared, {cleared.filter((d) => d.has_file).length} with a file ready</span>
        <button className={iconBtn} onClick={() => downloadFile('/marketing/datasheets/manifest', { method: 'POST', fallbackName: 'public-datasheets.json' }).catch((e) => setErr(e.message))}><Download size={12} /> Manifest (JSON)</button>
        <button className={iconBtn} onClick={() => downloadFile('/marketing/datasheets/manifest?format=csv', { method: 'POST', fallbackName: 'public-datasheets.csv' }).catch((e) => setErr(e.message))}><Download size={12} /> Manifest (CSV)</button>
      </div>
      {err && <p className="text-sm text-rose-600">{err}</p>}
      {!rows && !err && <p className="text-sm text-[var(--text-secondary)]">Loading…</p>}
      {rows && !rows.length && <Empty>The document library is empty. Add documents in Bids &amp; Specs first.</Empty>}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-xs text-[var(--text-secondary)]"><th className="py-1 pr-2">Document</th><th className="pr-2">Type</th><th className="pr-2">File</th><th className="pr-2">Cleared for public</th></tr></thead>
          <tbody>
            {(rows || []).map((d) => (
              <tr key={d.id} className="border-t border-[var(--border)]">
                <td className="py-2 pr-2"><div className="font-medium">{d.title}</div><div className="text-xs text-[var(--text-secondary)]">{[d.product, d.version, d.doc_date && fmtDate(d.doc_date)].filter(Boolean).join(' · ')}</div></td>
                <td className="pr-2 text-xs">{DOC_LABEL[d.doc_type] || d.doc_type}</td>
                <td className="pr-2 text-xs">{d.has_file ? <Chip tone="green">File ready</Chip> : <Chip tone="amber">No file yet</Chip>}</td>
                <td className="pr-2"><label className="flex items-center gap-2 text-xs"><input type="checkbox" disabled={busy === d.id} checked={!!d.cleared_public} onChange={() => toggle(d)} /> {d.cleared_public ? 'Cleared' : 'Internal only'}</label></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
